import * as React from 'react'
import { CaretDown } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import {
  Sheet,
  SheetOverlay,
  SheetHeader,
  SheetTitle,
  SheetBody,
  SheetFooter,
  type SheetSide,
} from '../core/sheet'
import { OverlayWrapper } from '../core/overlay'
import { DatePicker } from '../core/date-picker'
import { today, getLocalTimeZone, CalendarDate } from '@internationalized/date'
import type { DateValue } from 'react-aria-components'
import {
  AccordionRoot,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../core/accordion'
import { Badge } from '../core/badge'
import { Input } from '../core/input'
import { Radio, RadioGroup } from '../core/radio'
import { Popover, PopoverTrigger, PopoverContent } from '../core/popover'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '../core/input-group'

/**
 * FilterPanel — a right-anchored `Sheet` that holds a table's filters as a stack
 * of inline, expandable sections instead of a blocking modal of nested popovers.
 * Because the drawer sits beside the table (never over it), filters are meant to
 * apply *live*: each control writes straight to the consumer's filter state and
 * the table updates behind the open panel — so there's no Apply/Cancel footer.
 * The footer is instead where you see the result count and *save* the current
 * filter set as a named view (`footer` slot).
 *
 *   <FilterPanel
 *     trigger={<Button leftIcon={<Funnel />}>Filters</Button>}
 *     title="All filters"
 *     toolbar={<><SavedFilterSelect … /><SortMenu … /></>}
 *     defaultOpen={['quick', 'value']}
 *     footer={<FilterPanelFooter … />}
 *   >
 *     <FilterPanelSection value="quick" title="Quick Filters" activeCount={2}>
 *       <Checkbox label="Chain size 2+" … />
 *     </FilterPanelSection>
 *     <FilterPanelSection value="value" title="Lifetime Values">
 *       <FilterRangeField label="Lifetime Total Value" prefix="$" … />
 *     </FilterPanelSection>
 *   </FilterPanel>
 *
 * The panel owns only the chrome (open state, header, footer) — never the filter
 * values or the body layout. For the body, drop in either `FilterPanelSections`
 * (category-grouped accordion) or `FilterPanelList` (a flat list, e.g. an
 * alphabetical sort of every filter); a consumer can swap between them on a
 * "sort filters" toggle. Put any Core control inside (Checkbox, MultiCombobox,
 * Combobox, FilterRangeField, …).
 */

export interface FilterPanelProps {
  /** The element that opens the panel — typically a `<Button>`. */
  trigger: React.ReactElement
  /** Panel heading. */
  title?: React.ReactNode
  /** Header-row controls beneath the title (e.g. SavedFilterSelect, SortMenu). */
  toolbar?: React.ReactNode
  /** Footer content — result count, Save filter, Clear all. */
  footer?: React.ReactNode
  /** Edge the panel slides from (default `'right'`). */
  side?: SheetSide
  /** Width class for the sliding panel (default `'max-w-md'`). */
  widthClassName?: string
  /** Panel body — a `FilterPanelSections` or `FilterPanelList`. */
  children: React.ReactNode
}

export function FilterPanel({
  trigger,
  title = 'Filters',
  toolbar,
  footer,
  side = 'right',
  widthClassName = 'max-w-md',
  children,
}: FilterPanelProps) {
  return (
    <OverlayWrapper trigger={trigger}>
      <SheetOverlay side={side} className={widthClassName}>
        <Sheet
          aria-label={typeof title === 'string' ? title : 'Filters'}
          className="gap-0"
        >
          <SheetHeader className="gap-3 pb-4">
            <SheetTitle>{title}</SheetTitle>
            {/* Stack full-width: header controls fill the drawer, one per row. */}
            {toolbar && <div className="flex flex-col gap-2">{toolbar}</div>}
          </SheetHeader>
          <SheetBody className="p-0">{children}</SheetBody>
          {footer && (
            <SheetFooter className="flex-wrap items-center justify-between gap-y-2 border-t border-fbui-gray-200">
              {footer}
            </SheetFooter>
          )}
        </Sheet>
      </SheetOverlay>
    </OverlayWrapper>
  )
}
FilterPanel.displayName = 'FilterPanel'

export interface FilterPanelSectionsProps {
  /** Section ids (`FilterPanelSection.value`) open on first render. */
  defaultOpen?: string[]
  /** `FilterPanelSection`s. */
  children: React.ReactNode
  className?: string
}

/**
 * Category-grouped panel body: a full-bleed `flush` accordion of
 * `FilterPanelSection`s. The default layout — pair it with a "sort filters"
 * toggle that swaps to `FilterPanelList` for a flat alphabetical view.
 */
export function FilterPanelSections({
  defaultOpen,
  children,
  className,
}: FilterPanelSectionsProps) {
  return (
    <AccordionRoot
      type="multiple"
      defaultValue={defaultOpen}
      // `flush` = no outer box, just the between-section hairlines; the top rule
      // separates the first section from the header. Caret-only trigger so the
      // section header reads as a label with a discrete expander.
      variant="flush"
      triggerTarget="caret"
      className={cn('border-t border-fbui-gray-200', className)}
    >
      {children}
    </AccordionRoot>
  )
}
FilterPanelSections.displayName = 'FilterPanelSections'

export interface FilterPanelListProps {
  /** `FilterPanelRow`s — one per filter, in the desired (e.g. A–Z) order. */
  children: React.ReactNode
  className?: string
}

/**
 * Flat panel body: filters as a single divided list rather than grouped
 * sections. The counterpart to `FilterPanelSections` for an alphabetical /
 * ungrouped "sort filters" mode when there are too many filters to hunt through
 * categories. Wrap each filter in a `FilterPanelRow`.
 */
export function FilterPanelList({ children, className }: FilterPanelListProps) {
  return (
    <div
      className={cn(
        'divide-y divide-fbui-gray-200 border-t border-fbui-gray-200',
        className
      )}
    >
      {children}
    </div>
  )
}
FilterPanelList.displayName = 'FilterPanelList'

export interface FilterPanelRowProps {
  children: React.ReactNode
  className?: string
}

/** One padded row in a `FilterPanelList` — holds a single filter control. */
export function FilterPanelRow({ children, className }: FilterPanelRowProps) {
  return <div className={cn('px-6 py-4', className)}>{children}</div>
}
FilterPanelRow.displayName = 'FilterPanelRow'

export interface FilterPanelListHeaderProps {
  children: React.ReactNode
  className?: string
}

/**
 * A sticky group heading inside a `FilterPanelList` — e.g. the letter band in an
 * alphabetical view. Pins to the top of the scrolling body as its group scrolls
 * past, with an opaque band so rows don't show through. Emit one before each run
 * of `FilterPanelRow`s that belong to it.
 */
export function FilterPanelListHeader({
  children,
  className,
}: FilterPanelListHeaderProps) {
  return (
    <div
      className={cn(
        'sticky top-0 z-10 bg-fbui-gray-50 px-6 py-1.5 text-xs font-semibold uppercase tracking-wide text-fbui-gray-500',
        className
      )}
    >
      {children}
    </div>
  )
}
FilterPanelListHeader.displayName = 'FilterPanelListHeader'

export interface FilterPanelSectionProps {
  /** Unique id used for the accordion's open state + `defaultOpen`. */
  value: string
  /** Section heading. */
  title: React.ReactNode
  /** Number of active filters in this section — renders a brand count badge. */
  activeCount?: number
  /** The section's controls. */
  children: React.ReactNode
  /** Classes on the content wrapper. */
  className?: string
}

/**
 * One expandable section of a `FilterPanel` — a labelled accordion row whose
 * header shows an active-filter count. Controls render inline in the open panel
 * (no nested popover), stacked with a comfortable gap.
 */
export function FilterPanelSection({
  value,
  title,
  activeCount,
  children,
  className,
}: FilterPanelSectionProps) {
  return (
    <AccordionItem value={value}>
      <AccordionTrigger className="px-6">
        <span className="flex items-center gap-2">
          {title}
          {activeCount ? (
            <Badge color="primary" size="sm">
              {activeCount}
            </Badge>
          ) : null}
        </span>
      </AccordionTrigger>
      <AccordionContent className={cn('space-y-4 px-6 pb-5', className)}>
        {children}
      </AccordionContent>
    </AccordionItem>
  )
}
FilterPanelSection.displayName = 'FilterPanelSection'

export interface FilterFieldProps {
  /** Optional label rendered above the control. */
  label?: React.ReactNode
  /** The control (Combobox, MultiCombobox, Select, FilterRangeField, …). */
  children: React.ReactNode
  className?: string
}

/**
 * FilterField — a labelled wrapper for a single inline control in a
 * `FilterPanelSection`. Standardizes the small caption label above multi-selects,
 * comboboxes, and ranges so every field in the panel lines up the same way.
 */
export function FilterField({ label, children, className }: FilterFieldProps) {
  return (
    <div className={className}>
      {label != null && (
        <p className="mb-1.5 text-xs font-medium text-fbui-gray-700">{label}</p>
      )}
      {children}
    </div>
  )
}
FilterField.displayName = 'FilterField'

export interface FilterRange {
  min: number | null
  max: number | null
}

const EMPTY_RANGE: FilterRange = { min: null, max: null }

export interface FilterRangeFieldProps {
  /** Optional field label above the inputs. */
  label?: React.ReactNode
  /** Current value (controlled). */
  value?: FilterRange
  onChange?: (value: FilterRange) => void
  /** Currency/unit symbol — passing e.g. `"$"` switches to the currency layout. */
  prefix?: string
  /** Hard bounds, clamped on blur. */
  min?: number
  max?: number
}

const parseNum = (raw: string): number | null => {
  if (raw.trim() === '') return null
  const n = Number(raw)
  return Number.isNaN(n) ? null : n
}

/**
 * FilterRangeField — an inline min/max range, the drawer-native counterpart to
 * the popover `RangeFilter`. Two numeric inputs (a leading `$` addon when
 * `prefix` is set) write live to `onChange`; hard `min`/`max` bounds clamp on
 * blur, and min > max flags both inputs. Live-apply, so there's no footer.
 */
export function FilterRangeField({
  label,
  value = EMPTY_RANGE,
  onChange,
  prefix = '',
  min,
  max,
}: FilterRangeFieldProps) {
  const money = prefix !== ''
  const invalid =
    value.min != null && value.max != null && value.min > value.max
  const errorId = React.useId()

  const clamp = (n: number | null): number | null => {
    if (n == null) return null
    if (min != null && n < min) return min
    if (max != null && n > max) return max
    return n
  }

  const bound = (key: 'min' | 'max', aria: string) => {
    const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange?.({ ...value, [key]: parseNum(e.target.value) })
    const onBlur = () => onChange?.({ ...value, [key]: clamp(value[key]) })
    const status = invalid ? 'error' : undefined
    const current = value[key] ?? ''
    if (money) {
      return (
        <InputGroup size="sm" status={status} className="flex-1">
          <InputGroupAddon divided className="px-2.5 text-fbui-gray-500">
            {prefix}
          </InputGroupAddon>
          <InputGroupInput
            inputMode="decimal"
            placeholder="0.00"
            aria-label={aria}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid ? errorId : undefined}
            value={current}
            onChange={onChangeInput}
            onBlur={onBlur}
          />
        </InputGroup>
      )
    }
    return (
      <Input
        size="sm"
        inputMode="numeric"
        placeholder={key === 'min' ? 'Min' : 'Max'}
        aria-label={aria}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? errorId : undefined}
        status={status}
        value={current}
        onChange={onChangeInput}
        onBlur={onBlur}
        className="flex-1 text-center"
      />
    )
  }

  const labelText = typeof label === 'string' ? label : 'Range'

  return (
    <FilterField label={label}>
      <div className="flex items-center gap-2">
        {bound('min', `${labelText} minimum`)}
        <span className="shrink-0 text-fbui-gray-400">
          {money ? 'to' : '–'}
        </span>
        {bound('max', `${labelText} maximum`)}
      </div>
      {invalid && (
        <p
          id={errorId}
          role="alert"
          className="mt-1.5 text-xs text-fbui-red-600"
        >
          Min can’t be greater than max.
        </p>
      )}
    </FilterField>
  )
}
FilterRangeField.displayName = 'FilterRangeField'

export interface FilterDateRange {
  start: Date | null
  end: Date | null
}

const EMPTY_DATE_RANGE: FilterDateRange = { start: null, end: null }

const toCal = (d: Date | null): DateValue | null =>
  d ? new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate()) : null
const toJs = (d: DateValue | null): Date | null =>
  d ? new Date(d.year, d.month - 1, d.day) : null

export interface FilterDateRangeFieldProps {
  /** Optional field label above the inputs. */
  label?: React.ReactNode
  /** Current value (controlled). */
  value?: FilterDateRange
  onChange?: (value: FilterDateRange) => void
  /** Show the Last Week … Last Year quick-select preset chips (default true). */
  quickSelect?: boolean
}

/**
 * FilterDateRangeField — an inline start/end date range, the drawer-native
 * counterpart to the popover `DateRangeFilter` (and the date sibling of
 * `FilterRangeField`). Two typable MM/DD/YYYY `DatePicker`s (each with a
 * calendar) write live to `onChange`, with an optional row of Quick Select
 * preset chips (7 / 30 days · 3 / 6 mo · 1 yr) that fill start→today.
 * Live-apply, so there's no footer; the calendar cross-clamps (start ≤ end).
 */
export function FilterDateRangeField({
  label,
  value = EMPTY_DATE_RANGE,
  onChange,
  quickSelect = true,
}: FilterDateRangeFieldProps) {
  const t = today(getLocalTimeZone())
  const start = toCal(value.start)
  const end = toCal(value.end)
  const labelText = typeof label === 'string' ? label : 'Date'
  // `minValue`/`maxValue` only constrain the calendar popover — a hand-typed
  // start > end still commits, so flag it explicitly (mirrors FilterRangeField).
  const invalid =
    value.start != null && value.end != null && value.start > value.end
  const errorId = React.useId()

  // Abbreviated, consistent duration labels (the first two are genuinely
  // day-based so the label matches the math).
  const presets = [
    { label: '7 days', start: t.subtract({ days: 7 }) },
    { label: '30 days', start: t.subtract({ days: 30 }) },
    { label: '3 mo', start: t.subtract({ months: 3 }) },
    { label: '6 mo', start: t.subtract({ months: 6 }) },
    { label: '1 yr', start: t.subtract({ years: 1 }) },
  ]

  const clear = () => onChange?.({ start: null, end: null })
  // A preset is "active" when the value is exactly its start → today range, so
  // editing either date by hand (or clearing) deselects it automatically.
  const sameDay = (d: Date | null, c: DateValue) =>
    d != null &&
    d.getFullYear() === c.year &&
    d.getMonth() + 1 === c.month &&
    d.getDate() === c.day
  const activePreset =
    presets.find((p) => sameDay(value.start, p.start) && sameDay(value.end, t))
      ?.label ?? null

  return (
    <FilterField label={label}>
      <div className="space-y-2">
        {/* Start / End sit side by side; the `[role=group]` override stretches
            each DatePicker's inner Group (w-fit by default) to fill its column. */}
        <div className="grid grid-cols-2 gap-2 [&_[role=group]]:w-full">
          <div>
            <p className="mb-1 text-xs font-medium text-fbui-gray-500">
              Start date
            </p>
            <DatePicker
              size="sm"
              clearable
              invalid={invalid}
              aria-label={`${labelText} start date`}
              aria-describedby={invalid ? errorId : undefined}
              value={start}
              maxValue={end ?? undefined}
              onChange={(d) => onChange?.({ ...value, start: toJs(d) })}
              className="w-full"
            />
          </div>
          <div>
            <p className="mb-1 text-xs font-medium text-fbui-gray-500">
              End date
            </p>
            <DatePicker
              size="sm"
              clearable
              invalid={invalid}
              aria-label={`${labelText} end date`}
              aria-describedby={invalid ? errorId : undefined}
              value={end}
              minValue={start ?? undefined}
              onChange={(d) => onChange?.({ ...value, end: toJs(d) })}
              className="w-full"
            />
          </div>
        </div>
        {invalid && (
          <p id={errorId} role="alert" className="text-xs text-fbui-red-600">
            Start date can’t be after the end date.
          </p>
        )}
        {/* Clearing a set field is handled per-input by each DatePicker's
            trailing × (the `clearable` prop) — consistent with SearchBar — so
            there's no separate Clear button here. */}
        {quickSelect && (
          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
            {presets.map((p) => {
              const active = p.label === activePreset
              return (
                <button
                  key={p.label}
                  type="button"
                  // Toggle: picking the active preset again clears the range.
                  aria-pressed={active}
                  onClick={() =>
                    active
                      ? clear()
                      : onChange?.({ start: toJs(p.start), end: toJs(t) })
                  }
                  className={cn(
                    // px-2 py-0.5 = Badge `sm` proportions; text stays at the
                    // xs (12px) interactive floor (xxs is captions-only).
                    'rounded-full border px-2 py-0.5 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-fbui-ring-brand',
                    active
                      ? 'border-fbui-basil-600 bg-fbui-basil-50 text-fbui-basil-700'
                      : 'border-fbui-gray-300 text-fbui-gray-700 hover:border-fbui-gray-400 hover:bg-fbui-gray-50'
                  )}
                >
                  {p.label}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </FilterField>
  )
}
FilterDateRangeField.displayName = 'FilterDateRangeField'

export interface FilterRadioOption {
  value: string
  label: React.ReactNode
}

export interface FilterRadioFieldProps {
  /** Optional field label above the trigger. */
  label?: React.ReactNode
  /** The mutually-exclusive choices (e.g. `[{value:'no',label:'No'}, …]`). */
  options: FilterRadioOption[]
  /** Selected value (controlled); `null` = the "All" (unfiltered) option. */
  value?: string | null
  onChange?: (value: string | null) => void
  /** Label for the leading "no constraint" option (default "All"). */
  allLabel?: React.ReactNode
}

// Sentinel for the "All" radio — maps to a cleared (`null`) value. Prefixed so
// it can't collide with a real option value.
const RADIO_ALL = '__all__'

// Dropdown trigger styled to match the drawer's Combobox fields (h-9 rounded
// bordered box + trailing caret) so a radio filter reads like the others.
const radioTriggerClass = cn(
  'group flex h-9 w-full items-center justify-between gap-2 rounded-lg border bg-fbui-base-white pl-3 pr-2.5 text-left text-sm outline-none transition-colors',
  'border-fbui-gray-300 hover:border-fbui-gray-400',
  'focus-visible:border-fbui-basil-700 focus-visible:ring-4 focus-visible:ring-fbui-ring-brand',
  'data-[state=open]:border-fbui-basil-700 data-[state=open]:ring-4 data-[state=open]:ring-fbui-ring-brand'
)

/**
 * FilterRadioField — a single-select filter housed in a dropdown, like the
 * drawer's Combobox fields, but whose popover holds a `RadioGroup` (the
 * "Has Phone Number → No / Yes" pattern). Use it for a small fixed set of
 * mutually-exclusive choices where a Combobox's search would be overkill; reach
 * for `Combobox` (the `single` kind) when the option list is long or searchable.
 * A leading **All** radio represents "no constraint" (a `null` value) and is
 * selected by default — picking it clears the filter, so there's no separate
 * Reset. Picking any option writes live to `onChange` (no Apply — the drawer
 * applies live); the trigger shows the chosen option, muted while still on All.
 */
export function FilterRadioField({
  label,
  options,
  value = null,
  onChange,
  allLabel = 'All',
}: FilterRadioFieldProps) {
  const labelText = typeof label === 'string' ? label : 'Option'
  const selected = options.find((o) => o.value === value)
  return (
    <FilterField label={label}>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={labelText}
            className={radioTriggerClass}
          >
            <span
              className={cn(
                'min-w-0 flex-1 truncate',
                selected ? 'text-fbui-gray-800' : 'text-fbui-gray-400'
              )}
            >
              {selected ? selected.label : allLabel}
            </span>
            <CaretDown
              weight="bold"
              aria-hidden="true"
              className="size-4 shrink-0 text-fbui-gray-500 transition-transform group-data-[state=open]:rotate-180"
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          size="sm"
          align="start"
          className="w-[var(--radix-popover-trigger-width)]"
        >
          <RadioGroup
            aria-label={labelText}
            size="sm"
            // `null` selects the leading All option; picking All clears back to null.
            value={value ?? RADIO_ALL}
            onValueChange={(v) => onChange?.(v === RADIO_ALL ? null : v)}
          >
            <Radio value={RADIO_ALL} label={allLabel} />
            {options.map((o) => (
              <Radio key={o.value} value={o.value} label={o.label} />
            ))}
          </RadioGroup>
        </PopoverContent>
      </Popover>
    </FilterField>
  )
}
FilterRadioField.displayName = 'FilterRadioField'
